import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import Skeleton from '@/components/primitives/Skeleton.jsx';
import BackLink from '@/components/patterns/BackLink.jsx';
import EmptyState from '@/components/patterns/EmptyState.jsx';
import getAppByIdAndVersion from '@/redux/requests/apps/getAppByIdAndVersion.js';
import { appDefinitionForId, connections } from '@/redux/selectors/pieveSelectors/connectionSelectors.js';
import { appsMetadataItems, appsMetadataLoaded } from '@/redux/selectors/pieveSelectors/runtimeSelectors.js';
import ConnectForm from '../components/ConnectForm.jsx';
import ConnectionPath from '../components/ConnectionPath.jsx';
import ConnectionRow from '../components/ConnectionRow.jsx';
import SetupGuide from '../components/SetupGuide.jsx';
import resolveConnectMethod from '../lib/resolveConnectMethod.js';

/** One app: what it is, whether and how it connects, and the accounts of it you already connected. */
const AppDetailPage = ({ appSlug }) => {
  const navigate = useNavigate();
  const apps = useSelector(appsMetadataItems);
  const isCatalogLoaded = useSelector(appsMetadataLoaded);
  const summary = useMemo(() => apps.find((app) => app.slug === appSlug) || null, [apps, appSlug]);
  const version = summary ? summary.publishedVersion || summary.version : null;
  const definition = useSelector((state) => appDefinitionForId(state, summary?.id));
  const appConnections = useSelector(connections).filter((connection) => connection.appId === summary?.id);
  const isUnavailable = summary?.connectable === false;
  const method = useMemo(() => resolveConnectMethod(definition), [definition]);
  const pathState = isUnavailable ? 'unavailable' : appConnections.some((connection) => connection.status === 'valid') ? 'connected' : 'open';

  useEffect(() => {
    if (summary?.id && version && !isUnavailable) {
      getAppByIdAndVersion({ appId: summary.id, appVersion: version });
    }
  }, [summary?.id, version, isUnavailable]);

  const back = <BackLink to="/connections">Connections</BackLink>;

  if (!summary) {
    return (
      <div className="comp-page">
        {back}
        {isCatalogLoaded ? <EmptyState title="This app is not in the catalog" description="It may have been renamed or removed. Search the catalog for it, or ask Pieve to work with it." action={<Button onClick={() => navigate('/connections')}>Search apps</Button>} /> : <Skeleton shape="block" height={160} />}
      </div>
    );
  }

  const app = { id: summary.id, name: summary.name, version };

  return (
    <div className="comp-page connections-detail">
      {back}
      <header className="connections-detail__header">
        <ConnectionPath app={summary} state={pathState} />
        <h1 className="connections-detail__title">{summary.name}</h1>
        {summary.description && <p className="connections-detail__description">{summary.description}</p>}
      </header>

      {appConnections.length > 0 && (
        <section className="connections-section" aria-labelledby="app-accounts">
          <h2 id="app-accounts" className="connections-section__title">
            Your {summary.name} connections
          </h2>
          <ul className="connections-list">
            {appConnections.map((connection) => (
              <ConnectionRow key={connection.credentialId} connection={connection} appVersion={version} showApp={false} />
            ))}
          </ul>
        </section>
      )}

      {isUnavailable && (
        <section className="connections-unavailable" aria-labelledby="not-available">
          <h2 id="not-available" className="connections-section__title">
            Not available yet
          </h2>
          <p>
            {summary.name} signs in through its own site, and Pieve is still setting up that sign-in. You can ask Pieve about {summary.name} in the meantime; it will tell you what it can do without a connection.
          </p>
          <div>
            <Button onClick={() => navigate('/')}>Ask Pieve</Button>
          </div>
        </section>
      )}

      {!isUnavailable && !definition && <Skeleton shape="block" height={200} />}

      {!isUnavailable && definition && method && (
        <section className="connections-connect" aria-labelledby="connect-app">
          <div className="connections-connect__form">
            <h2 id="connect-app" className="connections-section__title">
              {appConnections.length > 0 ? `Connect another ${summary.name} account` : `Connect ${summary.name}`}
            </h2>
            {method.fields.length === 0 ? <p className="connections-connect__lead">{summary.name} needs no sign-in. Connecting makes it available to Pieve.</p> : null}
            <ConnectForm key={summary.id} app={app} method={method} />
          </div>
          <SetupGuide guide={method.auth.setupGuide} />
        </section>
      )}

      {!isUnavailable && definition && !method && (
        <section className="connections-unavailable" aria-labelledby="connect-elsewhere">
          <h2 id="connect-elsewhere" className="connections-section__title">
            Connect it from a conversation
          </h2>
          <p>{summary.name} signs in through its own site. Ask Pieve to connect it in a conversation.</p>
        </section>
      )}
    </div>
  );
};

export default AppDetailPage;
